import { useCallback, useLayoutEffect, useRef, type UIEvent } from "react";

const usePaginatedScroll = (itemCount: number, resetKey: string) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTopRef = useRef(0);
  const previousCountRef = useRef(itemCount);
  const previousResetKeyRef = useRef(resetKey);

  const rememberScrollPosition = useCallback((event: UIEvent<HTMLDivElement>) => {
    scrollTopRef.current = event.currentTarget.scrollTop;
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const resetChanged = previousResetKeyRef.current !== resetKey;
    const itemsAppended = itemCount > previousCountRef.current;

    previousCountRef.current = itemCount;
    previousResetKeyRef.current = resetKey;

    if (!container) return;

    if (resetChanged) {
      scrollTopRef.current = 0;
      container.scrollTop = 0;
      return;
    }

    if (!itemsAppended || scrollTopRef.current === 0) return;

    const savedScrollTop = scrollTopRef.current;
    const firstFrame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = savedScrollTop;
        }
      });
    });

    return () => cancelAnimationFrame(firstFrame);
  }, [itemCount, resetKey]);

  return { containerRef, rememberScrollPosition };
};

export default usePaginatedScroll;